import type { LandingLocale } from './content/landingContentTypes.js';
import type { StaticPageAudience } from './content/staticPageContent.js';
import { isPublicLandingPath, normalizeLandingPath } from './landingPageSeo.js';

export const LANDING_PRODUCTION_ORIGIN = 'https://www.vivaporta.org';

export const landingLocaleFromSearch = (search: URLSearchParams): LandingLocale => {
  const locale = search.get('lang');
  return locale === 'en' || locale === 'tr' ? locale : 'de';
};

export const landingAudienceFromUrl = (path: string, search: URLSearchParams): StaticPageAudience => {
  const audience = search.get('audience');
  if (audience === 'company' || audience === 'applicant') return audience;
  return normalizeLandingPath(path) === '/unternehmen' ? 'company' : 'applicant';
};

export const landingLocalizedUrl = (origin: string, path: string, locale: LandingLocale): string => {
  const url = new URL(normalizeLandingPath(path), origin);
  if (locale !== 'de') url.searchParams.set('lang', locale);
  return url.toString();
};

/** Localize public navigation only; preserve explicit targets, files, portal links and fragments. */
export const localizedLandingHref = (
  href: string,
  locale: LandingLocale,
  origin: string,
  audience?: StaticPageAudience,
): string => {
  if (!href || href.startsWith('#') || href.startsWith('?')) return href;
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin || !isPublicLandingPath(url.pathname)) return href;
    if (!url.searchParams.has('lang') && locale !== 'de') url.searchParams.set('lang', locale);
    if (audience === 'company' && !url.searchParams.has('audience') && landingAudienceFromUrl(url.pathname, url.searchParams) !== audience)
      url.searchParams.set('audience', audience);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

export const isRetiredLandingHref = (href: string): boolean => {
  try {
    const url = new URL(href, LANDING_PRODUCTION_ORIGIN);
    return ['www.vivaporta.org', 'vivaporta.org'].includes(url.hostname) &&
      ['/branchen/gastro', '/branchen/weitere-gesundheitsberufe'].includes(normalizeLandingPath(url.pathname));
  } catch {
    return false;
  }
};
